import { useState } from "react";
import { useTranslation } from "react-i18next";

export default function CTASection() {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>(Object());
  const { t } = useTranslation();

  const validate = (form: HTMLFormElement) => {
    const data = new FormData(form);
    const newErrors: Record<string, string> = {};
    if (!data.get("firstName")) newErrors.firstName = t("label.required");
    if (!data.get("lastName")) newErrors.lastName = t("label.required");
    if (!data.get("email")) newErrors.email = t("label.required");
    if (!data.get("country")) newErrors.country = t("label.required");
    if (!data.get("budget")) newErrors.budget = t("label.required");
    if (!data.get("goal")) newErrors.goal = t("label.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!validate(e.currentTarget)) {
      e.preventDefault();
      return;
    }
    setSubmitted(true);
  };

  return (
    <section id="inquiry" className="w-full bg-paper py-20 md:py-28 scroll-mt-20 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=Indigo-dyed%20Japanese%20noren%20fabric%20curtain%20woven%20cotton%20deep%20ai-blue%20color%20soft%20folds%20texture%20seamless%20minimal%20no%20text%20no%20objects&width=600&height=600&seq=noren-texture-noren&orientation=squarish')",
          backgroundSize: "cover",
        }}
      ></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-4">
              {t("cta.sectionLabel")}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-ink leading-[1.1] mb-5">
              {t("cta.title")}
              <br />
              <span className="italic font-normal text-ink/70">{t("cta.titleItalic")}</span>
            </h2>
            <p className="text-base text-ink/50 leading-relaxed max-w-lg mx-auto font-light">
              {t("cta.intro")}
            </p>
          </div>

          {submitted ? (
            <div className="bg-white border border-hairline p-10 md:p-12 text-center">
              <div className="w-16 h-16 bg-gold/10 flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-2xl text-gold"></i>
              </div>
              <h3 className="font-serif text-2xl font-semibold text-ink mb-3">
                {t("misc.thankYou")}
              </h3>
              <p className="text-ink/50 leading-relaxed font-light">
                {t("misc.responseTime")}
              </p>
            </div>
          ) : (
            <form
              action="https://readdy.ai/api/form/d86n54io8uvplcbvqeo0"
              method="POST"
              data-readdy-form
              id="noren-inquiry"
              onSubmit={handleSubmit}
              className="bg-white border border-hairline p-6 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-ink mb-2">
                    {t("cta.firstName")} <span className="text-vermilion">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    className={`w-full px-4 py-3 border bg-paper text-ink text-sm focus:outline-none focus:border-gold transition-colors duration-300 ${errors.firstName ? "border-vermilion" : "border-hairline"}`}
                    placeholder="James"
                  />
                  {errors.firstName && <p className="text-xs text-vermilion mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-2">
                    {t("cta.lastName")} <span className="text-vermilion">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    className={`w-full px-4 py-3 border bg-paper text-ink text-sm focus:outline-none focus:border-gold transition-colors duration-300 ${errors.lastName ? "border-vermilion" : "border-hairline"}`}
                    placeholder="Chen"
                  />
                  {errors.lastName && <p className="text-xs text-vermilion mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-ink mb-2">
                  {t("cta.email")} <span className="text-vermilion">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`w-full px-4 py-3 border bg-paper text-ink text-sm focus:outline-none focus:border-gold transition-colors duration-300 ${errors.email ? "border-vermilion" : "border-hairline"}`}
                  placeholder="james@example.com"
                />
                {errors.email && <p className="text-xs text-vermilion mt-1">{errors.email}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-ink mb-2">
                    {t("cta.country")} <span className="text-vermilion">*</span>
                  </label>
                  <select
                    name="country"
                    className={`w-full px-4 py-3 border bg-paper text-ink text-sm focus:outline-none focus:border-gold transition-colors duration-300 ${errors.country ? "border-vermilion" : "border-hairline"}`}
                  >
                    <option value="">{t("option.select")}</option>
                    <option value="US">{t("option.us")}</option>
                    <option value="UK">{t("option.uk")}</option>
                    <option value="CA">{t("option.ca")}</option>
                    <option value="AU">{t("option.au")}</option>
                    <option value="SG">{t("option.sg")}</option>
                    <option value="HK">{t("option.hk")}</option>
                    <option value="Other">{t("option.other")}</option>
                  </select>
                  {errors.country && <p className="text-xs text-vermilion mt-1">{errors.country}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-2">
                    {t("cta.budget")} <span className="text-vermilion">*</span>
                  </label>
                  <select
                    name="budget"
                    className={`w-full px-4 py-3 border bg-paper text-ink text-sm focus:outline-none focus:border-gold transition-colors duration-300 ${errors.budget ? "border-vermilion" : "border-hairline"}`}
                  >
                    <option value="">{t("option.select")}</option>
                    <option value="under-1m">{t("option.under1m")}</option>
                    <option value="1m-5m">{t("option.1mTo5m")}</option>
                    <option value="5m-10m">{t("option.5mTo10m")}</option>
                    <option value="10m-30m">{t("option.10mTo30m")}</option>
                  </select>
                  {errors.budget && <p className="text-xs text-vermilion mt-1">{errors.budget}</p>}
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-ink mb-2">
                  {t("cta.goal")} <span className="text-vermilion">*</span>
                </label>
                <select
                  name="goal"
                  className={`w-full px-4 py-3 border bg-paper text-ink text-sm focus:outline-none focus:border-gold transition-colors duration-300 ${errors.goal ? "border-vermilion" : "border-hairline"}`}
                >
                  <option value="">{t("option.select")}</option>
                  <option value="live-in">{t("option.liveIn")}</option>
                  <option value="renovation">{t("option.renovation")}</option>
                  <option value="rental">{t("option.rental")}</option>
                  <option value="not-sure">{t("option.notSure")}</option>
                </select>
                {errors.goal && <p className="text-xs text-vermilion mt-1">{errors.goal}</p>}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-ink mb-2">
                  {t("cta.message")}
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-hairline bg-paper text-ink text-sm focus:outline-none focus:border-gold transition-colors duration-300 resize-none"
                  placeholder={t("cta.messagePlaceholder")}
                ></textarea>
                <p className="text-xs text-ink/30 mt-1 font-light">{t("label.maxChars")}</p>
              </div>

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-ink text-paper text-sm font-medium px-6 py-3.5 rounded-full hover:bg-indigo transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                {t("btn.submit")}
                <i className="ri-arrow-right-up-line text-sm"></i>
              </button>

              <p className="text-xs text-ink/30 text-center mt-5 font-light">
                {t("cta.privacy")}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}